import {
  Activity as ActivityIcon,
  ArrowRightLeft,
  BadgeCheck,
  Eye,
  OctagonAlert,
  Package,
  Layers,
  Split,
  Pencil,
  RefreshCw,
  Tag,
  Undo2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

export interface ActivityTypeConfig {
  label: string;
  Icon: LucideIcon;
}

// Maps every action-log `type` (enum_certificate_action_type) to a friendly
// label + icon for the owner-facing Activity timeline. Raw scan events
// (scan / invalid_scan) are excluded server-side (they live in the Scans tab).
//
// VERA-978: lifted out of ActivityTabContent so the filter drawer can build its
// options from the same source. Keeping it in the tab would have made the tab and
// the drawer import each other.
export const ACTIVITY_TYPE_MAP: Record<string, ActivityTypeConfig> = {
  create_certificate: { label: 'Certificate created', Icon: BadgeCheck },
  init_transfer: { label: 'Transfer initiated', Icon: ArrowRightLeft },
  scan_transfer: { label: 'Transfer scanned', Icon: ArrowRightLeft },
  confirm_transfer: { label: 'Transfer confirmed', Icon: ArrowRightLeft },
  accept_transfer: { label: 'Transfer accepted', Icon: ArrowRightLeft },
  admin_transfer: { label: 'Transferred by Vera', Icon: ArrowRightLeft },
  cancel_transfer: { label: 'Transfer canceled', Icon: XCircle },
  cancel_initiated_transfer: { label: 'Transfer canceled', Icon: XCircle },
  admin_cancel_transfer: { label: 'Transfer canceled by Vera', Icon: XCircle },
  request_cancel_transfer: { label: 'Transfer cancellation requested', Icon: XCircle },
  confirm_request_cancel_transfer: { label: 'Transfer cancellation confirmed', Icon: XCircle },
  reject_request_cancel_transfer: { label: 'Transfer cancellation rejected', Icon: XCircle },
  // `loose` is the schema's spelling for "reported lost" — see the
  // enum_certificate_action type. Unmapped, it fell through to `humanizeType`
  // and the row read "Loose", which is not a word the product uses anywhere and
  // reads as a rendering fault. Its two siblings were mapped and it was not.
  loose: { label: 'Reported lost', Icon: OctagonAlert },
  stole: { label: 'Reported stolen', Icon: OctagonAlert },
  return: { label: 'Returned', Icon: Undo2 },
  replace_tag: { label: 'Tag replaced', Icon: Tag },
  // VRG-232: certificate groups. Owner-facing wording deliberately avoids the internal
  // words "parent"/"child" — an owner sees a set of components, not a data model.
  add_to_group: { label: 'Added to a group', Icon: Layers },
  remove_from_group: { label: 'Removed from a group', Icon: Layers },
  split_group: { label: 'Group split', Icon: Split },
  CertificateStatusChange: { label: 'Status changed', Icon: RefreshCw },
  update_certificate: { label: 'Certificate updated', Icon: Pencil },
  update: { label: 'Certificate updated', Icon: Pencil },
  assign_tag: { label: 'Tag assigned', Icon: Tag },
  assign_product: { label: 'Product assigned', Icon: Package },
  public_access: { label: 'Public link viewed', Icon: Eye },
};

export const humanizeType = (type: string) =>
  type
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, char => char.toUpperCase());

export const getActivityTypeConfig = (type: string): ActivityTypeConfig =>
  ACTIVITY_TYPE_MAP[type] || { label: humanizeType(type), Icon: ActivityIcon };

export const getActivityTypeLabel = (type: string): string => getActivityTypeConfig(type).label;

export interface ActivityTypeGroup {
  key: string;
  label: string;
  /** The raw action-log types this group stands for. */
  types: string[];
}

/**
 * VERA-978: the filter offers GROUPS, not the 21 raw types. Eleven of those types
 * are transfer states a reader thinks of as one thing ("transfers"), and a list of
 * twenty near-identical rows is not a filter anyone uses. Selecting a group selects
 * every type inside it.
 */
export const ACTIVITY_TYPE_GROUPS: ActivityTypeGroup[] = [
  {
    key: 'transfers',
    label: 'Transfers',
    types: Object.keys(ACTIVITY_TYPE_MAP).filter(type => type.includes('transfer')),
  },
  {
    key: 'status',
    label: 'Status changes',
    // `loose` belongs here for the same reason `stole` does. Omitting it meant
    // filtering to "Status changes" silently HID every reported-lost event —
    // a filter that drops matching rows is worse than an unmapped label,
    // because nothing on screen says anything is missing.
    types: ['CertificateStatusChange', 'loose', 'stole', 'return'],
  },
  {
    key: 'certificate',
    label: 'Certificate',
    types: ['create_certificate', 'update_certificate', 'update'],
  },
  {
    key: 'tags_products',
    label: 'Tags and products',
    types: ['assign_tag', 'assign_product', 'replace_tag'],
  },
  {
    key: 'access',
    label: 'Access',
    types: ['public_access'],
  },
];

/**
 * Which groups a set of raw types represents. One derivation used by BOTH the drawer
 * (to restore its checkboxes) and the tab (to count filter badges), so the checkbox
 * state and the badge can never disagree.
 */
export const getSelectedGroupKeys = (selectedTypes: string[]): string[] => {
  if (selectedTypes.length === 0) return [];
  return ACTIVITY_TYPE_GROUPS.filter(group =>
    group.types.some(type => selectedTypes.includes(type))
  ).map(group => group.key);
};
